// ─── Routes ──────────────────────────────────────────────────────────────────
export const ROUTE_PATHS = {
  HOME: '/',
} as const;

// ─── Section IDs (Anchor Navigation) ─────────────────────────────────────────
export const SECTION_IDS = {
  HERO: 'hero',
  PROBLEM: 'problema',
  SOLUTION: 'solucao',
  HOW_IT_WORKS: 'como-funciona',
  MARKET: 'mercado',
  DIFFERENTIATION: 'diferenciais',
  IMPACT: 'impacto',
  CTA: 'cta-final',
} as const;

// ─── Scroll Helper ────────────────────────────────────────────────────────────
export function scrollToSection(id: string): void {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

// ─── Nav Links ────────────────────────────────────────────────────────────────
export interface NavLink {
  label: string;
  sectionId: string;
}

export const NAV_LINKS: NavLink[] = [
  { label: 'O Problema', sectionId: SECTION_IDS.PROBLEM },
  { label: 'Solução', sectionId: SECTION_IDS.SOLUTION },
  { label: 'Como Funciona', sectionId: SECTION_IDS.HOW_IT_WORKS },
  { label: 'Mercado', sectionId: SECTION_IDS.MARKET },
  { label: 'Impacto', sectionId: SECTION_IDS.IMPACT },
];

// ─── Stats Data ───────────────────────────────────────────────────────────────
export interface Stat {
  value: string;
  label: string;
  description: string;
}

export const STATS: Stat[] = [
  { value: '80%', label: 'Economia Informal', description: 'da economia angolana opera no sector informal' },
  { value: '69%', label: 'Sem Conta Bancária', description: 'dos angolanos não têm acesso a serviços bancários' },
  { value: '+$25B', label: 'Mercado Potencial', description: 'estimativa da economia informal angolana anualmente' },
  { value: '12M+', label: 'Comerciantes', description: 'operando sem identidade financeira digital em Angola' },
];

// ─── Problem Points ───────────────────────────────────────────────────────────
export interface ProblemPoint {
  title: string;
  description: string;
  iconPath: string;
}

export const PROBLEM_POINTS: ProblemPoint[] = [
  {
    title: 'Sem Acesso a Crédito',
    description: 'Milhões de comerciantes angolanos são invisíveis ao sistema financeiro. Sem histórico, sem crédito.',
    iconPath: 'M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z',
  },
  {
    title: 'Dependência de Numerário',
    description: 'O dinheiro físico domina os mercados do Roque Santeiro ao Benfica, criando riscos e ineficiências.',
    iconPath: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z',
  },
  {
    title: 'Sem Identidade Financeira',
    description: 'Transacções em dinheiro não deixam rasto. Comerciantes trabalham décadas sem construir historial.',
    iconPath: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z',
  },
  {
    title: 'Baixa Confiança Digital',
    description: 'Interfaces complexas e em línguas estrangeiras criam barreiras para comerciantes com baixa literacia digital.',
    iconPath: 'M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 8.25h3m-3 3h3m-3 3h3',
  },
];

// ─── Solution Pillars ─────────────────────────────────────────────────────────
export interface Pillar {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  color: string;
  glowColor: string;
}

export const PILLARS: Pillar[] = [
  {
    number: '01',
    title: 'Smart POS',
    subtitle: 'Sunmi P2 — Tecnologia ao Alcance de Todos',
    description: 'Terminal inteligente desenhado para o comerciante angolano. Interface simples em português, aceita Multicaixa, transferências e cartões. Funciona mesmo com literacia digital básica.',
    features: ['Aceita Multicaixa Express', 'Interface em Português', 'Funciona offline', 'Impressão de recibos', 'Zero taxa de activação'],
    color: 'from-purple-600 to-violet-800',
    glowColor: 'rgba(124, 58, 237, 0.4)',
  },
  {
    number: '02',
    title: 'Pontuação de Crédito Comportamental',
    subtitle: 'O Seu Histórico de Transacções Constrói o Seu Futuro',
    description: 'Cada transacção no POS constrói a sua identidade financeira. O nosso algoritmo analisa padrões de venda para criar um score de crédito único, desbloqueando acesso a microcrédito.',
    features: ['Score baseado em dados reais', 'Desbloqueia microcrédito', 'Cresce com o negócio', 'Sem garantias tradicionais', 'Visível em tempo real'],
    color: 'from-blue-600 to-cyan-700',
    glowColor: 'rgba(37, 99, 235, 0.4)',
  },
  {
    number: '03',
    title: 'Crédito de Stock',
    subtitle: 'Receba o Stock Agora, Pague Após Vender',
    description: 'O modelo "Pague conforme vende" permite ao comerciante receber inventário sem capital inicial. Pague apenas quando vender, com condições ajustadas ao fluxo real de cada negócio.',
    features: ['Stock sem entrada inicial', 'Pagamento pós-venda', 'Condições flexíveis', 'Parceiros fornecedores', 'Expansão progressiva'],
    color: 'from-cyan-600 to-teal-700',
    glowColor: 'rgba(8, 145, 178, 0.4)',
  },
];

// ─── How It Works Steps ───────────────────────────────────────────────────────
export interface Step {
  step: string;
  title: string;
  description: string;
  iconPath: string;
}

export const HOW_IT_WORKS_STEPS: Step[] = [
  {
    step: '1',
    title: 'Registe o Seu Negócio',
    description: 'Processo simples de onboarding. O comerciante regista-se e recebe o POS Sunmi P2 activado e pronto a usar.',
    iconPath: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  },
  {
    step: '2',
    title: 'Comece a Aceitar Pagamentos',
    description: 'Aceite Multicaixa Express, QR Code e transferências. Clientes pagam via app PAGA FÁCIL sem dinheiro físico.',
    iconPath: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
  },
  {
    step: '3',
    title: 'Construa o Seu Historial',
    description: 'Cada transacção é registada e analisada. O seu score de crédito cresce automaticamente com o volume de negócios.',
    iconPath: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  },
  {
    step: '4',
    title: 'Aceda ao Crédito',
    description: 'Com histórico suficiente, receba ofertas de microcrédito e crédito de stock personalizadas ao seu perfil de negócio.',
    iconPath: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    step: '5',
    title: 'Expanda o Seu Negócio',
    description: 'Com crédito acessível e gestão digital, o negócio cresce. Da barraca ao estabelecimento. Da sobrevivência à prosperidade.',
    iconPath: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
  },
];

// ─── Market Stats ─────────────────────────────────────────────────────────────
export interface MarketStat {
  value: string;
  label: string;
  subtext: string;
}

export const MARKET_STATS: MarketStat[] = [
  { value: '+$25B', label: 'Economia Informal em Angola', subtext: 'estimativa conservadora anual' },
  { value: '$2.5T', label: 'Economia Informal na África', subtext: 'maior sector não-servido do mundo' },
  { value: '12M+', label: 'Comerciantes em Angola', subtext: 'sem acesso a pagamentos digitais' },
  { value: '$65B', label: 'Mercado Fintech África 2030', subtext: 'crescimento projectado 13x' },
];

// ─── Comparison Data ──────────────────────────────────────────────────────────
export interface ComparisonRow {
  feature: string;
  others: boolean | string;
  kufuta: boolean | string;
}

export const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: 'Pagamentos Digitais', others: true, kufuta: true },
  { feature: 'Crédito Baseado em Dados', others: false, kufuta: true },
  { feature: 'Crédito de Stock', others: false, kufuta: true },
  { feature: 'Desenhado para B2B Informal', others: false, kufuta: true },
  { feature: 'Pontuação de Crédito Comportamental', others: false, kufuta: true },
  { feature: 'App PAGA FÁCIL para Clientes', others: false, kufuta: true },
  { feature: 'Interface em Português Local', others: false, kufuta: true },
  { feature: 'Modelo Offline + Online', others: false, kufuta: true },
];

// ─── Impact Metrics ───────────────────────────────────────────────────────────
export interface ImpactMetric {
  sdg: string;
  title: string;
  description: string;
  color: string;
}

export const IMPACT_METRICS: ImpactMetric[] = [
  { sdg: 'ODS 1', title: 'Erradicação da Pobreza', description: 'Ao fornecer acesso a crédito e ferramentas financeiras, KufutaPay+ eleva o rendimento de famílias angolanas vulneráveis.', color: 'from-red-500 to-orange-600' },
  { sdg: 'ODS 8', title: 'Trabalho Digno e Crescimento Económico', description: 'Formalização progressiva da economia informal, criando condições para emprego sustentável e crescimento de PMEs angolanas.', color: 'from-purple-500 to-violet-600' },
  { sdg: 'ODS 9', title: 'Indústria, Inovação e Infraestrutura', description: 'Construção de infraestrutura financeira digital inclusiva em Angola, habilitando a economia informal a crescer.', color: 'from-blue-500 to-cyan-600' },
  { sdg: 'ODS 10', title: 'Redução das Desigualdades', description: 'Inclusão financeira real para os segmentos mais excluídos da sociedade angolana, democratizando o acesso a serviços financeiros.', color: 'from-cyan-500 to-teal-600' },
];
